import type { FlowNode, SendMsgDingTalkRobotConfig } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, PAGE_LINK_OPTION, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import LinkMsg from './LinkMsg'
import RichTextMsg from './RichTextMsg'
import * as SC from './styles'
import TextMsg from './TextMsg'

type DingTalkRobotCardProps = {
    config: SendMsgDingTalkRobotConfig
}

const CMxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const DingTalkRobotCard: React.FC<DingTalkRobotCardProps> = ({ config }) => {
    const { msgtype, text, link, at, markdown } = config
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractDataAt = useMemo(() => extractDataFn(at.atContent), [at.atContent])

    const nodesEditorAt = useNodesData(extractDataAt.nodeIds) as FlowNode[]

    const upstreamOptionsEditorAt = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesEditorAt || []),
                      allUpstreamNodes: nodesEditorAt
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesEditorAt]
    )

    const component = useMemo(() => {
        switch (msgtype) {
            case 'text': {
                return text ? <TextMsg text={text} /> : null
            }
            case 'markdown': {
                return markdown ? <RichTextMsg markdown={markdown} /> : null
            }
            case 'link': {
                return link ? <LinkMsg link={link} /> : null
            }

            default: {
                return null
            }
        }
    }, [msgtype, text, link, markdown])

    const dingTalkAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'DING_TALK_ROBOT'), [otherPlatformList])

    if (!dingTalkAccount?.configurationComplete || (!component && extractDataAt.objects.length === 0)) {
        return null
    }

    return (
        <CM.CardContainer>
            {component}
            {extractDataAt.objects.length > 0 && ['text', 'markdown'].includes(msgtype) && (
                <CM.CardItemContainer>
                    <SC.TypeName>提醒人员</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataAt.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsEditorAt,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}
        </CM.CardContainer>
    )
}

export default DingTalkRobotCard
